/**
 * Nederlandse weergave voor bedragen, hoeveelheden en eenheden uit engine/library.
 */
import { titleCaseFromSlug } from './catalog-labels';

const UNIT_MAP: Record<string, string> = {
  hour: 'u',
  hours: 'u',
  hr: 'u',
  h: 'u',
  piece: 'st',
  pieces: 'st',
  pcs: 'st',
  pc: 'st',
  st: 'st',
  stuks: 'st',
  stuk: 'st',
  ea: 'st',
  each: 'st',
  m2: 'm²',
  m3: 'm³',
  sqm: 'm²',
  'm²': 'm²',
  'm³': 'm³',
  m1: 'm',
  lm: 'm',
  l_m: 'm',
  kg: 'kg',
  ton: 'ton',
  t: 'ton',
  day: 'dag',
  days: 'dagen',
  week: 'wk',
  weeks: 'wk',
  lump: 'post',
  lump_sum: 'post',
  ls: 'post',
  percent: '%',
  pct: '%',
};

/** Maatcode of afkorting → gebruikelijke NL-notatie. */
export function formatMeasureUnitNl(unit: string | undefined | null): string {
  const raw = (unit ?? '').trim();
  if (!raw) return '—';
  const key = raw.toLowerCase().replace(/\s+/g, '_');
  if (UNIT_MAP[key]) return UNIT_MAP[key];
  if (UNIT_MAP[raw.toLowerCase()]) return UNIT_MAP[raw.toLowerCase()];
  if (/^[a-z0-9_]+$/i.test(raw) && raw.includes('_') && !raw.includes(' ')) {
    return titleCaseFromSlug(raw);
  }
  return raw;
}

/** Hoeveelheid: gehele getallen zonder decimalen; anders NL-locale met zinvolle precisie. */
export function formatQuantityNl(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return '—';
  const abs = Math.abs(n);
  const rounded = Math.round(n);
  if (abs >= 1000 || Math.abs(n - rounded) < 1e-9) {
    return new Intl.NumberFormat('nl-NL', { maximumFractionDigits: 0 }).format(rounded);
  }
  if (abs >= 10) {
    return new Intl.NumberFormat('nl-NL', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(n);
  }
  return new Intl.NumberFormat('nl-NL', { minimumFractionDigits: 0, maximumFractionDigits: 4 }).format(n);
}

/** Bedrag excl. in euro, NL (bijv. € 1.234,56). */
export function formatEuroNl(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) {
    return new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(0);
  }
  return new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(n);
}

/**
 * Omschrijving uit prijsregel: technische slug (light_site_tools) → leesbare titel.
 * Bestaande zinnen met spaties blijven ongewijzigd.
 */
export function humanizePricingLineDescription(text: string | undefined | null): string {
  const t = (text ?? '').trim();
  if (!t) return '—';
  if (/^[a-z][a-z0-9_]*$/i.test(t) && t.includes('_')) return titleCaseFromSlug(t);
  return t;
}
